// packages/api-client/src/client.ts
// Обёртка над сгенерированным клиентом: задаёт BASE + токен, экспортирует сервисы.
import { OpenAPI } from './core/OpenAPI';
import { DefaultService } from './services/DefaultService';
import { StaffService } from './services/StaffService';
import { TestsStaffService } from './services/TestsStaffService';
import { AuthService } from './services/AuthService';
import { CompaniesService } from './services/CompaniesService';
import { AssessmentService } from './services/AssessmentService';
import { AnalyticsService } from './services/AnalyticsService';
import { HiringService } from './services/HiringService';
import { InvitationsService } from './services/InvitationsService';
import { MatchService } from './services/MatchService';
import { TestResultsService } from './services/TestResultsService';
import { TestsService } from './services/TestsService';

const DEFAULT_BASE = process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:3000/api';
const TOKEN_KEY = 'tb_token';
const REFRESH_KEY = 'tb_refresh';

export function configureApi(token?: string) {
  OpenAPI.BASE = DEFAULT_BASE;
  OpenAPI.TOKEN = token || undefined;
  OpenAPI.WITH_CREDENTIALS = false;
}

// ----- токен: localStorage + cookie (cookie переживает переходы/перезагрузки) -----
function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}
function writeCookie(name: string, value: string) {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${60 * 60 * 24 * 7}; SameSite=Lax`;
}
function clearCookie(name: string) {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
}

export function setToken(token: string | null) {
  if (typeof window !== 'undefined') {
    if (token) {
      window.localStorage.setItem(TOKEN_KEY, token);
      writeCookie(TOKEN_KEY, token);
    } else {
      window.localStorage.removeItem(TOKEN_KEY);
      clearCookie(TOKEN_KEY);
    }
  }
  OpenAPI.TOKEN = token || undefined;
}

export function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(TOKEN_KEY) ?? readCookie(TOKEN_KEY);
}

export function setRefreshToken(token: string | null) {
  if (typeof window !== 'undefined') {
    if (token) {
      window.localStorage.setItem(REFRESH_KEY, token);
    } else {
      window.localStorage.removeItem(REFRESH_KEY);
    }
  }
}

export function getRefreshToken(): string | null {
  if (typeof window !== 'undefined') {
    return window.localStorage.getItem(REFRESH_KEY);
  }
  return null;
}

/** Refresh the access token using the stored refresh token. Returns new accessToken or null.
 *  Singleton: concurrent callers share the same in-flight promise (prevents refresh storms). */
let _refreshInFlight: Promise<string | null> | null = null;

export async function refreshAccessToken(): Promise<string | null> {
  // If a refresh is already in progress, wait for it
  if (_refreshInFlight) return _refreshInFlight;

  _refreshInFlight = (async () => {
    const rt = getRefreshToken();
    if (!rt) return null;
    try {
      const res = await fetch(`${DEFAULT_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: rt }),
      });
      if (!res.ok) return null;
      const json = await res.json();
      const newToken = json?.data?.accessToken;
      if (newToken) {
        setToken(newToken);
        const newRefresh = json?.data?.refreshToken;
        if (newRefresh) setRefreshToken(newRefresh);
        return newToken;
      }
      return null;
    } catch {
      return null;
    }
  })();

  try {
    return await _refreshInFlight;
  } finally {
    _refreshInFlight = null; // clear after completion
  }
}

/** Clear all tokens (logout). */
export function clearAuth() {
  setToken(null);
  setRefreshToken(null);
}

export { DefaultService, StaffService, TestsStaffService, AuthService, CompaniesService, AssessmentService, AnalyticsService, HiringService, InvitationsService, MatchService, TestResultsService, TestsService, OpenAPI };
